import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { createUseStyles } from "react-jss";
import { EmpdContext } from "../../../../Context";
import { useMsal } from '@azure/msal-react'
import axios from '../../../../axiosConfig';
import useScreenScroll from "../../../../Hooks/useScreenScroll";
import DashboardNavigationBar from "./Components/DashboardNavigationBar";
import WelcomeBanner from "./Components/WelcomeBanner";
import ContentWrapper from "../../../../Components/ContentWrapper";
import CaptureConsentCard from "./Components/CaptureConsentCard";
import ClinicalIntake from "./Components/ClinicalIntake";
import AnnouncementsCard from "./Components/AnnouncementsCard";
import UserData from "../../../../Interfaces/UserData";
import DataSummaryCard from "./Components/DataSummaryCard";
import NonUsCitizenCard from "./Components/NonUsCitizenCard";
import { cloneDeep } from "lodash";
import { RotatingLines } from 'react-loader-spinner';
import WelcomeModal from '../../../../Components/WelcomeModal';
import DeclinedModal from '../../../../Components/DeclinedModal';
import NotAuthorized from '../../../../Components/NotAuthorized';
import { isProvider } from '../../../../utils';

const useStyles = createUseStyles({
  root: {},
  dashboardWrapper: {
    background: "#f8f8f8",
    height: "100%",
    padding: "50px 0px",
  },
  dashboardContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "top",
  },
  dashboardCard: {
    flexGrow: 0,
	flexShrink: 0,
    flexBasis: "46.5%",
  },
  icon: {
    width: 30,
    height: "auto",
    marginRight: 10,
  },
  announcementHeading: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
});

const checkB2CSession = async (msalInstance: any, login:(msalInstance: any) => Promise<UserData | null>) => {
  if( msalInstance.getAllAccounts().length > 0 ) {
    try {
        await msalInstance.acquireTokenSilent({
            scopes: ["openid"],
            account: msalInstance.getAllAccounts()[0],
            prompt: "none",
        });
        return true;
    } catch (error) {
        console.error(error)
        return false;
    }
  } else {
    //const userClaims = await login(msalInstance)
    return false
  }
}

const pollB2CSession = (msalInstance: any, setActiveSession:(a: boolean) => void, login:(msalInstance: any) => Promise<UserData | null>, interval = 300000) => {

  const intervalId = setInterval(async () => {
    const sessionActive = await checkB2CSession(msalInstance, login);
        setActiveSession(sessionActive)
        if (sessionActive) {
            // Perform actions for active session
            console.log("Session is active");
        } else {
            // Perform actions for inactive session
            console.log("Session is inactive");
        }
    }, interval);
    return(intervalId)
}

const checkSign = async (activeSession: boolean, isResearchParticipant: boolean, declined: boolean, setEnvelope: (value: any) => void, getCookie:(value:string) => void, fetchPatientData:(value: any) => void, userData: any, setUpdateFields:(value: any) => void, setIsResearchParticipant:(value: boolean) => void, sign:() => void, value: boolean) => {
	
    if ( activeSession && !isResearchParticipant && !declined && value){
//        await sign(userData, setEnvelope)
        await sign()
    } else if( activeSession && isResearchParticipant ) {
        const envelopeId = localStorage.getItem('envelopeId')
        if( envelopeId ) {
            setEnvelope({envelopeId: envelopeId})
        }
        await getCookie('registered')
        fetchPatientData(userData.userObjectId)
    }
}

const useMyContext = () => {
    const context = useContext(EmpdContext)
    if( !context ) {
        throw new Error("No provider found for Context")
    }
    return context
}

const PatientDashboard = () => {
  const context = useMyContext()
  const { isLoading, isPatientLoading, authUser, setAuthUser, isResearchParticipant, setIsResearchParticipant, updateFields, setUpdateFields, writeFlags, userData, setUserData, fetchPatientData, envelope, setEnvelope, getCookie, activeSession, setActiveSession, login, pdfDoc, setPdfDoc, addNewUser, fetchNonParticipantData, patientResult, sign, isUsPerson, schemaName, demoTable, conditionSet, user } = context
  const [declined, setDeclined] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const { instance: msalInstance } = useMsal()
  
  /*
  useEffect(() => {
  	fetchNamespace()
  }, [])
*/

  useEffect(() => {
  	if( userData !== null ) {
		getCookie('registered')
	}
  }, [userData])

  useEffect(() => {
    if( (patientResult === null && conditionSet !== undefined) || (Object.keys(patientResult).length  === 0 && !isResearchParticipant && conditionSet !== undefined)) {
        fetchNonParticipantData()
    }

    if( userData === null ) {
        const fetchUserInfo = async () => {
            if( authUser.username !== '' ) {
                msalInstance.setActiveAccount(authUser)
                setActiveSession(await checkB2CSession(msalInstance, login))
                setAuthUser(authUser)
            } else if( localStorage.getItem('authUser') ) {
                const authUserData = localStorage.getItem('authUser')
                if( authUserData ) {
                    const user = JSON.parse(authUserData)
                    msalInstance.setActiveAccount(user.account)
                    setActiveSession(await checkB2CSession(msalInstance, login))
                    setAuthUser(JSON.parse(authUserData))
                }
            }

            if( localStorage.getItem('email') ) {
                const data = {
                    givenName: localStorage.getItem('givenName') ?? "",
                    familyName: localStorage.getItem('familyName') ?? "",
                    email: localStorage.getItem('email') ?? "",
                    token: "", //searchParams.get('code') ?? "",
                    userObjectId: localStorage.getItem('userObjectId') ?? "",
                    included: localStorage.getItem('included') === 'true' ?? false,
                    consented: localStorage.getItem('consented') === 'true' ?? false,
                    newUser: localStorage.getItem('newUser') === 'true' ?? false,
                }
                setUserData(data)
                if( localStorage.getItem('envelopeId') ) {
                    setEnvelope({ envelopeId: localStorage.getItem('envelopeId') || ''})
                }
            }

        }
        fetchUserInfo()
    }  else {
        const setSession = async() => {
            setActiveSession(await checkB2CSession(msalInstance, login))
        }
        setSession()
    }

    getCookie('registered')
    const intervalId = pollB2CSession(msalInstance, setActiveSession, login)

    return () => {
        clearInterval(intervalId)
    }
  },[conditionSet])

  useEffect(() => {
        if( Object.keys(patientResult).length > 0 && 
		    Object.keys(patientResult.categories).length > 0 &&
			patientResult.categories['General Demography'].records.length > 0 &&
			patientResult.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.consented`).value === null && 
			searchParams.get('event') && searchParams.get('event') === 'signing_complete' ) {
            setIsResearchParticipant(true)
        }
  }, [patientResult])
  
  useEffect(() => {
    const fetchData = async () => {
        if( isResearchParticipant && Object.keys(patientResult).length > 0 && 
			Object.keys(patientResult.categories).length > 0 &&
			patientResult.categories['General Demography'].records.length > 0 &&
			patientResult.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.consented`).value !== 'True') {
				writeFlags({[`${schemaName}.${demoTable}.consented`]: true})
        } 

        if( isResearchParticipant && userData && Object.keys(patientResult).length === 0) {
            await getCookie('registered')
            fetchPatientData(userData.userObjectId)
        }
    }
    fetchData()
  },[isResearchParticipant, isPatientLoading])

  useEffect(() => {
    if( authUser && activeSession && !isResearchParticipant && localStorage.getItem('envelopeId') && !pdfDoc) {
        const fetchEnvelope = async () => {
            const env = {
                    envelopeId: localStorage.getItem('envelopeId'),
            }

			try {
	            const data = await axios.get(`/empd/docusign/data/envelope/${env.envelopeId}`, {responseType: 'blob'})
    	        const file = new Blob([data.data],{type: 'application/pdf'})
        	    const fileUrl = URL.createObjectURL(file)
            	setPdfDoc(fileUrl)

    	        //  Once the user has consented, cretae permissions for them to use the backends
	            if( authUser !== undefined && authUser.idTokenClaims !== undefined &&
    	            authUser.idTokenClaims.emails !== undefined && authUser.idTokenClaims.emails[0]) {
	                await addNewUser({
						type: 'patient',
                	    last_name: authUser.idTokenClaims.family_name as string,
            	        first_name: authUser.idTokenClaims.given_name as string,
        	            email_address: authUser.idTokenClaims.emails[0] as string,
    	                city: authUser.idTokenClaims.city as string,
	                    state_name: authUser.idTokenClaims.state as string,
                	    zipcode: authUser.idTokenClaims.postalCode as string,
            	        iss: authUser.idTokenClaims.iss as string,
        	            oid: authUser.idTokenClaims.oid as string,
    	                file: file
	                })
					
					if( searchParams.get('event') && searchParams.get('event') === 'signing_complete' ) {
						if( Object.keys(updateFields).length > 0 ) {
							const updateFieldsCopy = cloneDeep(updateFields)
							updateFieldsCopy[`${schemaName}.${demoTable}.consented`] = true
							setUpdateFields(updateFieldsCopy)
						} else {
							setUpdateFields({
								[`${schemaName}.${demoTable}.consented`]: true
							})
						}
						setIsResearchParticipant(true)
					}
            	}
			} catch(error) {
				console.error(`Couldn't find envelope ID ${env.envelopeId}`)
			}
        }
        fetchEnvelope()
    }
  }, [envelope])

  useScreenScroll();
  const classes = useStyles();

  if( !isLoading && isProvider(user) ) {
	return <NotAuthorized />
  }

  const showSummaryCard = userData && !isResearchParticipant && !declined && searchParams.get('event') && searchParams.get('event') === 'signing_complete' ? true : false

  if( isUsPerson ) {
  	return (
	    <div>
		  <WelcomeModal />
		  <DeclinedModal />
    	  <DashboardNavigationBar/>
	      <WelcomeBanner />
    	  <div className={classes.dashboardWrapper}>
        	<ContentWrapper>
	          <div className={classes.dashboardContent}>
			  	{(isLoading || isPatientLoading) ? (
					<div className='top-center'>
                    <RotatingLines
                        strokeColor="#152E54"
                        strokeWidth="3"
                        animationDuration="1.0"
                        width="48"
                        visible={true}
                    />
					</div>
                ) : (
					<>
    	          {!isResearchParticipant && !showSummaryCard ? (
				  	<div>
        	          <CaptureConsentCard
            	        className={classes.dashboardCard}
                	    onSetResearchParticipant={(value: boolean) => {
                    	    checkSign(activeSession, isResearchParticipant, declined, setEnvelope, getCookie, fetchPatientData, userData, setUpdateFields, setIsResearchParticipant, sign, value)
	                    }}
    	              />
					  <ClinicalIntake className={classes.dashboardCard}/>
					  </div>
        	        ) : (
					<div>
            	      <DataSummaryCard className={classes.dashboardCard} />
					  <ClinicalIntake className={classes.dashboardCard}/>
					  </div>
	                )
    	          }</>)}
        	    <AnnouncementsCard className={classes.dashboardCard} />
	          </div>
    	    </ContentWrapper>
	      </div>
    	</div>
  	);
  } else {
	  return (
    	<div>
		  <DashboardNavigationBar />
    	  <WelcomeBanner />
	      <div className={classes.dashboardWrapper}>
    	    <ContentWrapper>
        	  <div className={classes.dashboardContent}>
		  		{isLoading ? (
					<div className='center'>
		                <RotatingLines
    		                strokeColor="#152E54"
        		            strokeWidth="3"
            		        animationDuration="1.0"
                		    width="48"
                    		visible={true}
	                	/>
					</div>
    	        ) : ( 
					<>
					  	<NonUsCitizenCard />
        		    	<AnnouncementsCard className={classes.dashboardCard} />
					</>
				)}
			  </div>
			</ContentWrapper>
		  </div>
		</div>
  	)
  }
};

export default PatientDashboard;
